// src/store/slices/featureFlagsSlice.ts
// Feature flags state management for Central Foundation.
//
// INVARIANT: Feature flags fail closed.
// If evaluation fails, all flags default to false.
// Unknown flags return false.

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';

// ============================================================
// Types
// ============================================================

type FeatureFlagsStatus = 'idle' | 'loading' | 'ready' | 'error';

interface FeatureFlagsState {
  /** Feature flags as simple boolean map (transformed from Central response) */
  flags: Record<string, boolean>;
  /** Loading/ready/error status */
  status: FeatureFlagsStatus;
  /** SSE connection status */
  sseConnected: boolean;
  /** Timestamp of last successful evaluation */
  evaluatedAt: string | null;
}

// ============================================================
// Initial State
// ============================================================

const initialState: FeatureFlagsState = {
  flags: {},
  status: 'idle',
  sseConnected: false,
  evaluatedAt: null,
};

// ============================================================
// Slice
// ============================================================

const featureFlagsSlice = createSlice({
  name: 'featureFlags',
  initialState,
  reducers: {
    /**
     * Set loading state before evaluation.
     */
    setFeatureFlagsLoading(state) {
      state.status = 'loading';
    },

    /**
     * Set feature flags after successful evaluation.
     * Expects transformed boolean map (not raw Central response).
     */
    setFeatureFlags(state, action: PayloadAction<Record<string, boolean>>) {
      state.flags = action.payload;
      state.status = 'ready';
      state.evaluatedAt = new Date().toISOString();
    },

    /**
     * Set error state on evaluation failure.
     * INVARIANT: Fail closed — all flags default to false.
     */
    setFeatureFlagsError(state) {
      state.flags = {};
      state.status = 'error';
    },

    /**
     * Clear all feature flags (e.g., on logout or workspace switch).
     */
    clearFeatureFlags(state) {
      state.flags = {};
      state.status = 'idle';
      state.evaluatedAt = null;
    },

    /**
     * Update SSE connection status.
     */
    setSseConnected(state, action: PayloadAction<boolean>) {
      state.sseConnected = action.payload;
    },
  },
});

// ============================================================
// Exports
// ============================================================

export const {
  setFeatureFlagsLoading,
  setFeatureFlags,
  setFeatureFlagsError,
  clearFeatureFlags,
  setSseConnected,
} = featureFlagsSlice.actions;

export default featureFlagsSlice.reducer;

// ============================================================
// Selectors
// ============================================================

export const selectFeatureFlags = (state: RootState) => state.featureFlags.flags;

export const selectFeatureFlagsStatus = (state: RootState) => state.featureFlags.status;

export const selectSseConnected = (state: RootState) => state.featureFlags.sseConnected;

export const selectFeatureFlagsEvaluatedAt = (state: RootState) =>
  state.featureFlags.evaluatedAt;

/**
 * Select a single feature flag by key.
 * INVARIANT: Returns false for unknown flags (fail closed).
 */
export const selectFeatureFlag = (flagKey: string) => (state: RootState) =>
  state.featureFlags.flags[flagKey] ?? false;
